<template>
  <div class="mb-4 sidebar" :class="{ 'bo_narrow-col': narrowColumn }" ref="column">
    <div class="component">
      <div class="bo_categories-header d-flex align-items-center flex-wrap">
        <span class="element-title">Grupper for valg og tillegg</span>
        <span v-if="!isEmployee" class="ms-auto text-nowrap">
          <!-- See Categories.vue: the bare glyph did not read as something you could press. -->
          <button type="button" class="bo_btn_secondary bo_btn_compact me-2" @click="goToSortAddOnGroupsPage">
            <img src="@/assets/images/sort_order.png" width="18" class="bo_btn_icon" alt="" />Rekkefølge
          </button>
          <button type="button" class="bo_btn_secondary bo_btn_compact me-2" @click="goToAddOnTemplatesPage"> Bibliotek </button>
          <button type="button" class="bo_btn" @click="goToAddAddOnGroupPage"> + Legg til gruppe </button>
        </span>
      </div>
      <div class="content">
        <ul class="bo_categoriesList">
          <li v-for="addOnGroup in addOnGroups" :key="addOnGroup.id" class="addOnGroup-row">
            <!-- The header is its own row. The expanded list of choices below is a sibling, so the
                 <li> itself must NOT be the flex container — it would lay the whole list out beside
                 the name. -->
            <div class="bo_row-head">
            <a v-if="!isEmployee" class="remove-addOnGroup-btn bo_remove-action"
               @click="removeAddOnGroup(addOnGroup)">
              <img src="@/assets/images/remove.png" width="21" title="Slett gruppe"/>
            </a>

            <span class="add-on-group-name" @click="expandOrCollapseContent(addOnGroup.id)">{{ addOnGroup.name }}
              <span class="bo_count">({{ getAddOns(addOnGroup.id).length }})</span>
            </span>
            <span v-if="isAddOnGroupExpanded(addOnGroup.id)" class="ms-2 icon-btn" @click="expandOrCollapseContent(addOnGroup.id)"><font-awesome-icon :icon="['fas', 'chevron-up']"/></span>
            <span v-else class="ms-2 icon-btn" @click="expandOrCollapseContent(addOnGroup.id)"><font-awesome-icon :icon="['fas', 'chevron-down']"/></span>

            <div v-if="!isEmployee" class="edit-category-btn bo_row-actions">
              <a class="me-3 icon-btn"
                 @click="goToSortAddOnsOfAddOnGroupPage(addOnGroup.id)">
                <img src="@/assets/images/sort_order.png" width="23" title="Endre rekkefølgen av valg og tillegg" />
              </a>
              <a class="icon-btn" @click="editAddOnGroup(addOnGroup.id)">
                <img src="@/assets/images/edit.png" width="21" title="Rediger gruppe"/>
              </a>
              <span class="ms-3">
                <button type="button" class="bo_btn_secondary bo_btn_compact" @click="goToAddAddOnPage(addOnGroup.id)"> + Legg til valg </button>
              </span>
            </div>
            <!-- The same three actions, for when the column is too narrow to show them and the name
                 at once. Which of the two is displayed is decided in CSS by the column's width. -->
            <button v-if="!isEmployee" type="button" class="icon-btn bo_row-overflow"
                    aria-label="Flere valg" @click.stop="toggleGroupMenu(addOnGroup, $event)">
              <font-awesome-icon :icon="['fas', 'ellipsis-v']"/>
            </button>
            </div>

            <div :class="isAddOnGroupExpanded(addOnGroup.id)? 'addOns' : 'd-none'">
              <div v-for="addOn in getAddOns(addOnGroup.id)" :key="addOn.id" class="addOn">
                <div class="d-flex align-items-center">
                    <div v-if="!isEmployee">
                        <a class="remove-addOn-btn bo_remove-action"
                           @click="removeAddOn(addOn)">
                          <img src="@/assets/images/remove.png" width="21" title="Slett valg"/>
                        </a>
                    </div>
                    <div>
                      <div>
                        {{ addOn.name }}
                        <span v-if="addOn.templateId" class="ms-1" title="Fra tilbehørsbiblioteket. Navn og pris styres av biblioteket.">
                          <font-awesome-icon :icon="['fas', 'link']"/>
                        </span>
                        <span v-if="addOnSoldOutBadge(addOn)" class="bo_sold-out-badge ms-1" :title="addOnSoldOutBadge(addOn).title">{{ addOnSoldOutBadge(addOn).label }}</span>
                      </div>
                      <div><span class="price">kr {{ getPriceWithVat(addOn.price) }} </span><span> (med {{vat.takeOut}}% mva for takeaway)</span></div>
                      <div v-if="useFunctionality === 'POS' || useFunctionality === 'KIOSK_TAKEAWAY' || useFunctionality === 'ALL'">
                        <span class="price">kr {{ getPriceDineInWithVat(addOn.priceDineIn) }} </span><span> (med {{vat.dineIn}}% mva for spise her)</span>
                      </div>
                    </div>
                    <div v-if="!isEmployee" class="edit-category-btn">
                      <a class="icon-btn" @click="editAddOn(addOnGroup.id, addOn.id)">
                        <img src="@/assets/images/edit.png" width="21" title="Rediger valg"/>
                      </a>
                    </div>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
  <!--
    Fixed rather than absolute, and at the root rather than in the row: the add-on column scrolls on
    its own (.component .content), and a menu positioned inside it is cut off at the column's edge.
    Same pattern as the device actions menu in Mdm.vue, and the same reason.
  -->
  <template v-if="openGroupMenu">
    <div class="bo_row-menu-backdrop" @click="openGroupMenu = null"></div>
    <div class="bo_row-menu" :style="{ top: openGroupMenu.top + 'px', right: openGroupMenu.right + 'px' }">
      <a class="bo_row-menu__item" @click="groupMenuAction('sort')">Endre rekkefølge på valg</a>
      <a class="bo_row-menu__item" @click="groupMenuAction('edit')">Rediger gruppe</a>
      <a class="bo_row-menu__item" @click="groupMenuAction('add')">+ Legg til valg</a>
    </div>
  </template>

  <ConfirmDialogue ref="confirmDialogue"></ConfirmDialogue>
</template>

<script>
import ConfirmDialogue from '@/components/other/ConfirmDialogue.vue'
import ProductService from "@/services/product/ProductService";
import ItemUtil from "@/util/ItemUtil";
import MerchantUtil from "@/util/MerchantUtil";
import SoldOutUtil from "@/util/SoldOutUtil";
import UserUtil from "@/util/UserUtil";

export default {
  components: {ConfirmDialogue},
  props: {
    addOnGroups: Array,
    addOns: Array,
    vat: Object,
    searchActive: { type: Boolean, default: false }, //When searching, all listed groups are expanded to show the matches
    soldOut: { type: Object, default: null } //SoldOutUtil.index: where each thing is sold out, per location
  },
  emits: ['openPanel', 'refresh'],
  computed: {
    isEmployee(){
      return UserUtil.isEmployee();
    },
    useFunctionality(){
      return MerchantUtil.useFunctionality();
    }
  },
  data () {
    return {
      expandedAddOnGroupIds: [],
      // {group, top, right} while the three-dots menu is open on a row; null otherwise.
      openGroupMenu: null,
      // True once this column is too narrow to show a row's actions and its name at the same time.
      narrowColumn: false
    }
  },
  /*
   * Watch the COLUMN's width, not the window's.
   *
   * This started as a @container query, which says the same thing in one line — and the WebView in
   * the MDM app on the D3 mini does not know @container, so nothing collapsed on the one device the
   * feature exists for. ResizeObserver has been in every Chromium since 2018 and is what the fleet
   * can be relied on to have.
   *
   * The window's width is the wrong measure either way: this column is about a third of the page, so
   * the same viewport gives it a very different width depending on the layout.
   */
  mounted() {
    this.measureColumn();
    // $refs.column, NOT $el: this component has several root nodes — the column, the three-dots menu
    // and the confirm dialogue — so Vue's root is a fragment and $el is the comment node marking it.
    if (typeof ResizeObserver !== 'undefined' && this.$refs.column) {
      this._columnObserver = new ResizeObserver(() => this.measureColumn());
      this._columnObserver.observe(this.$refs.column);
    } else {
      this._onResize = () => this.measureColumn();
      window.addEventListener('resize', this._onResize, { passive: true });
    }
  },
  beforeUnmount() {
    if (this._columnObserver) {
      this._columnObserver.disconnect();
    }
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
    }
  },
  methods: {
      /*
       * Below this the three actions and the group name cannot share a row: the actions win and the
       * name is squeezed to nothing. Measured on the column itself.
       */
      measureColumn(){
        const column = this.$refs.column;
        if(column){
          this.narrowColumn = column.getBoundingClientRect().width < 520;
        }
      },

      /*
       * The three-dots menu on a group row. Positioned from the button's own rectangle because the
       * menu is fixed — see the comment on the markup for why it cannot live inside the row.
       */
      toggleGroupMenu(addOnGroup, event){
        if(this.openGroupMenu && this.openGroupMenu.group.id === addOnGroup.id){
          this.openGroupMenu = null;
          return;
        }
        const rect = event.currentTarget.getBoundingClientRect();
        this.openGroupMenu = {
          group: addOnGroup,
          top: rect.bottom + 4,
          right: Math.max(8, window.innerWidth - rect.right)
        };
      },

      groupMenuAction(action){
        const group = this.openGroupMenu && this.openGroupMenu.group;
        this.openGroupMenu = null;
        if(!group){
          return;
        }
        if(action === 'sort'){ this.goToSortAddOnsOfAddOnGroupPage(group.id); }
        else if(action === 'edit'){ this.editAddOnGroup(group.id); }
        else if(action === 'add'){ this.goToAddAddOnPage(group.id); }
      },

      getPriceWithVat(priceWithoutVat){
        return ItemUtil.getPriceWithVat(priceWithoutVat, this.vat);
      },
      getPriceDineInWithVat(priceWithoutVat){
        return ItemUtil.getPriceDineIn(priceWithoutVat || 0, this.vat);
      },
      /**
       * Sold out through the linked product or the library entry, per location: a library add-on marked
       * sold out at one location is still sold at the others, and the badge says where.
       */
      addOnSoldOutBadge(addOn){
        return SoldOutUtil.addOnBadge(addOn, this.soldOut);
      },
      getAddOns(addOnGroupId) {
        let addOns = [];
        for (let i = 0; i < this.addOns.length; i++) {
          let addOn = this.addOns[i];
          if(addOn.addOnGroupId == addOnGroupId){
            addOns.push(addOn)
          }
        }
        return addOns;
      },
      expandOrCollapseContent(addOnGroupId){
         if(this.expandedAddOnGroupIds.includes(addOnGroupId)){//currently expanded
           //Collapse
            const index = this.expandedAddOnGroupIds.indexOf(addOnGroupId);
            this.expandedAddOnGroupIds.splice(index, 1);
         } else {
            //expand
            this.expandedAddOnGroupIds.push(addOnGroupId);
         }
      },
      isAddOnGroupExpanded(addOnGroupId){
          return this.searchActive || this.expandedAddOnGroupIds.includes(addOnGroupId);
      },
      goToSortAddOnGroupsPage(){
        this.$emit('openPanel', 'addOnGroupsSort', {});
      },
      goToSortAddOnsOfAddOnGroupPage(addOnGroupId){
        this.$emit('openPanel', 'addOnsOfAddOnGroupSort', { addOnGroupId: addOnGroupId });
      },
      goToAddAddOnGroupPage(){
        this.$emit('openPanel', 'addOnGroupCreate', {});
      },
      goToAddOnTemplatesPage(){
        this.$emit('openPanel', 'addOnTemplates', {});
      },
      goToAddAddOnPage(addOnGroupId){
        this.$emit('openPanel', 'addOnCreate', { addOnGroupId: addOnGroupId });
      },
      async removeAddOnGroup(addOnGroup){
        const ok = await this.$refs.confirmDialogue.show({
          title: 'Slett gruppen "' + addOnGroup.name + '"',
          message: 'Vil du slette gruppen "' + addOnGroup.name + '"' + ' og dens tilhørende valg og tillegg?',
          okButton: 'Slett',
          cancelButton: 'Avbryt'
        })
        if(ok) {
          ProductService.deleteAddOnGroup(addOnGroup).then(
              (response) => {
                if(response.data != null){
                  this.$emit('refresh');
                }else {
                  console.log("Deleting addOnGroup failed");
                }
              },
              (error) => {
                console.log(error);
              }
          );
        } else {
          //Cancel, do nothing
        }
      },
    async removeAddOn(addOn){
      const ok = await this.$refs.confirmDialogue.show({
        title: 'Slett valget "' + addOn.name.trim() + '"',
        message: 'Vil du slette valget "' + addOn.name.trim() + '"' + '?',
        okButton: 'Slett',
        cancelButton: 'Avbryt'
      })
      if(ok) {
        ProductService.deleteAddOn(addOn).then(
            (response) => {
              if(response.data != null){
                this.$emit('refresh');
              }else {
                console.log("Deleting addOnGroup failed");
              }
            },
            (error) => {
              console.log(error);
            }
        );
      } else {
        //Cancel, do nothing
      }
    },
    editAddOnGroup(addOnGroupId){
      console.log('Rediger gruppe - addOnGroupId:', addOnGroupId);
      this.$emit('openPanel', 'addOnGroupEdit', { addOnGroupId: addOnGroupId });
    },
    editAddOn(addOnGroupId, addOnId){
      this.$emit('openPanel', 'addOnEdit', { addOnGroupId: addOnGroupId, addOnId: addOnId });
    }
  }
}
</script>

<style scoped>
  /**
   * lg: 992px breakpoint or larger
   */
  @media screen and (min-width: 992px) {
    .bo_categories-header{
      padding-right: 5px;
    }

    .sidebar {
      position: sticky;
      top: var(--bo-nav-offset, 0px);   /* clears the pinned navbar */
    }
    .component {
      display: grid;
      grid-template-rows: auto 1fr auto;
    }
    .component .content {
      overflow-y: auto;
    }
    .sidebar .component {
      max-height: calc(100vh - 5px);
    }
  }
</style>
